import { StateCreator } from "zustand";
import {
HeatedSegment,} from "./properties.ts";
import { ESRLogin } from "../../../ESR-API/login.ts";
import { ESRFinancialPlan } from "../../../ESR-API/financial_plan.ts";
import { ESRProfitabilityCalculation} from "../../../ESR-API/profitability_calculation.ts";

import { ESRBasicData } from "../../../ESR-API/basic_data.ts";
import { ESRCheckZip} from "../../../ESR-API/check_zip.ts";

import { ESRCalculateEnergyCosts, ESRCalculateEnergyCostsBody } from "../../../ESR-API/calculate_energy_costs.ts";
import { ESRGetCosts } from "../../../ESR-API/get_costs.ts";

import { BuildingTypeKeys, ESRGetTradeMeasures, ESRGetTradeMeasuresResponse } from "../../../ESR-API/get_trade_measure.ts";
import { PropertySlice } from "./property-slice.ts";
import { AppSlice } from "./app-slice.ts";
import { round_to_01, round_to_10 } from "../functions.ts";

const LIVE : boolean = false;
const DEBUG_API_USER = "febis";
const DEBUG_API_PASSWORD = "L6g$ds71Us%5";
const BASEURL = "https://esr.fe-bis.de/de/main";

let menu_timeout : number | undefined = undefined;

export interface FinancialPlanItem {
  subsidy: number;
  total_costs: number;
  planned_costs: number;
}
export interface FinancialPlan {
  window: FinancialPlanItem;
  photovoltaics: FinancialPlanItem;
  solar_thermal: FinancialPlanItem;
  heating_system: FinancialPlanItem;
  roof_insulation: FinancialPlanItem;
  floor_insulation: FinancialPlanItem;
  facade_insulation: FinancialPlanItem;
  ventilation_system: FinancialPlanItem;
}
export interface ApiSlice {
  session_token?: string;
  measure_lables: Record<string, string>;
  trade_measures: ESRGetTradeMeasuresResponse;
  pv?: {
    area: number,
    hint: string,
    elec_annual_savings: number,
    elec_self_consumption: number
  },
  pv_hint?: string;
  // pv_area?: number,
  // pv_elec_annual_savings?: number,
  // pv_elec_self_consumption?: number,
  payback_period?: number;
  heating_demand_kwh?: number;
	electricity_consumption_kwh?: number;
  annual_energy_consumption_kwh_adjusted?: number;
  annual_energy_consumption_kwh_default?: number;
  annual_energy_consumption_kwh_planned?: number;
  annual_heating_costs_adjusted?: number;
  annual_heating_costs_default?: number;
  annual_heating_costs_planned?: number;
  annual_pv_production_kwh_adjusted?: number;
  annual_pv_production_kwh_default?: number;
  annual_pv_production_kwh_planned?: number;
  energy_efficiency_class_adjusted?:  number;
  energy_efficiency_class_adjusted_level?: number;
  energy_efficiency_class_default?:  number;
  energy_efficiency_class_default_level?: number;
  energy_efficiency_class_planned?: number;
  energy_efficiency_class_planned_level?: number;
  co2_tax_savings?: number;
  total_beg_subsidy?: number;
  savings_percentage?: number;
  co2_emission_current?: number;
  co2_emission_planned?: number;
  savings_over_lifetime?: number;
  total_investment_costs?: number;
  monthly_possible_savings?: number;
  period_under_review_savings?: number;
  period_under_review_energy_savings?: number;
  annual_savings_on_energy_costs?: number;
  energy_efficiency_class_current?: number;
  annual_energy_consumption_kwh_current?: number;
  annual_profit_loss?:number;
  monthly_profit_loss?:number;
  return_on_investment?:number;
  heizung_isfp?: boolean;
  keller_isfp?: boolean;
  dach_isfp?: boolean;
  fenster_isfp?: boolean;
  fassade_isfp?: boolean;
  solarthermie_isfp?: boolean;
  photovoltaik_isfp?: boolean;
  lueftung_isfp?: boolean;
  financial_plan: FinancialPlan;
  financial_plan_dummy: FinancialPlan & {version: number};
  total_final_costs: number;
  total_planned_costs: number;
  total_possible_subsidy: number;
  building_system: {
    costs: number,
    subsidy: number,
    percentage: number,
    max_funding: number
  },
  building_envelope: {
      isfp: boolean,
      costs: number,
      subsidy: number,
      percentage: number,
      max_funding: number
  },
  region_name: string;
  heizung_tmp: string;
  api_login: () => Promise<void>;
  api_basic_data: () => Promise<void>;
  api_check_zip:(plz: number) => Promise<boolean>;
  api_calculate_energy_costs: () => Promise<void>;
  api_calculate_energy_costs_dummy: () => Promise<void>;
  api_profitability_calculation: () => Promise<void>;
  api_financial_plan: () => Promise<void>;
  api_financial_plan_dummy: () => Promise<void>;
  api_recalculate: () => Promise<void>;
  api_get_costs: (type: string, code: string) => Promise<void>;
}

export const createApiSlice: StateCreator<AppSlice & ApiSlice & PropertySlice, [], [], AppSlice> = (
  set,
  get,
) => ({
  api_login: async () => {
    let session_token;

    if(LIVE){
      const json = await (await fetch(BASEURL+'/session.php')).json();
      if(json === undefined) {return;}
      session_token = json.session_token;
    }else{
      const request_login = new ESRLogin(DEBUG_API_USER, DEBUG_API_PASSWORD);
      const response_login = await request_login.request().catch((e) => {
          console.error("LOGIN ERROR:",e);
      });
      session_token = response_login.session_token;
    }    
    
    if(session_token === undefined) {return;}
    set((_) => ({session_token}));
    
    const request_measures = new ESRGetTradeMeasures(session_token);
    const response_measures = await request_measures.request().catch((e) => {
        console.error(e);
    });

    if (!response_measures){ return; }
    const measure_lables = Object.keys(response_measures.measures).flatMap((m)=>[
      ...Object.keys(response_measures.measures[m].options.current_state).map((n) => {
        const x = response_measures.measures[m].options.current_state[n];
        return {
         [x.code]:x.label,
        }
      }),
      ...Object.keys(response_measures.measures[m].options.planned_state).map((n) => {
        const x = response_measures.measures[m].options.planned_state[n];
        return {
         [x.code]:x.label,
        }
      })
      ]
    ).reduce((acc, x)=>({...acc, ...x}), {});
    set((_) => ({
      trade_measures: response_measures,
      measure_lables
    }));
  },

  api_calculate_energy_costs: async () =>{
    const session_token = get().session_token;
    if(session_token == undefined){
      return;
    }

    function remove_undefined_from_object(obj: ESRCalculateEnergyCostsBody){
      Object.keys(obj).forEach(measure => {
          ["current_state","planned_state"].forEach((a)=>{
            ["code", 
              "income_bonus",
              "replacement",
              "cost_estimate", 
              "installation_year", 
              "isfp",
              "natural_refrigerant",
              "heizung_natural_refrigerant",
              "heizung_natural_refrigerant_future",
              "annual_yield_kWh",
              "roof_pitch",
              "orientation",
              "battery_storage"].forEach((b)=>{
              if (obj[measure][a][b] === undefined || 
              obj[measure][a][b] === "" || 
              obj[measure][a][b] === null || 
              obj[measure][a][b] === 0
            ){
                delete obj[measure][a][b];
              }
            });   

          if(obj[measure][a] !== undefined && Object.keys(obj[measure][a]).length == 0){
            delete obj[measure][a];
          }     
        });

        if(Object.keys(obj[measure]).length == 0){
          delete obj[measure];
        }
      });
    }

    const renovation_measures = {
       "facade_insulation": {
           "current_state": {
                "code": get().fassade,
                "installation_year": get().fassade_datum
            },
            "planned_state": {
              "code": get().fassade_future,
              "cost_estimate": get().fassade_kosten,
              "isfp" : get().fassade_isfp
            },
          },
          "roof_insulation": {
           "current_state": {
              "code": get().dach,
              "installation_year": get().dach_datum,
            },
            "planned_state": {
              "code": get().dach_future,
              "cost_estimate": get().dach_kosten,
              "isfp": get().dach_isfp,
            }
          },
          "floor_insulation": {
            "current_state": {
              "code": get().keller,
              "installation_year":  get().keller_datum
            },
            "planned_state": {
              "code": get().keller_future,
              "cost_estimate":  get().keller_kosten,
              "isfp": get().keller_isfp,
            }
          },
          "window": {
            "current_state": {
              "code": get().fenster,
              "installation_year":  get().fenster_datum
            },
            "planned_state": {
              "code": get().fenster_future,
              "cost_estimate":  get().fenster_kosten,
              "isfp": get().fenster_isfp,
            }
          },
          "heating_system": {
            "current_state": {
              "code": get().heizung,
              "installation_year": get().heizung_datum,
              "natural_refrigerant": get().heizung_natural_refrigerant,
            },
            "planned_state": {
              "code": get().heizung_future,
              "cost_estimate": get().heizung_kosten,
              "natural_refrigerant": get().heizung_natural_refrigerant_future,
              "isfp": get().heizung_isfp,
              "income_bonus": get().income_gt_4k, 
              "replacement": get().heizung_wird_ausgetauscht,
              "private_residential_property": get().privat,
              "dust_emission": get().heizung_dust_emission,
              "hot_water": get().hot_water,
            }
          },
          "ventilation_system": {
            "current_state": {
              "code": get().lueftung,
              "installation_year": get().lueftung_datum
            },
            "planned_state": {
              "code": get().lueftung_future,
              "cost_estimate":  get().lueftung_kosten,
              "isfp": get().lueftung_isfp
            }
          },
          "solar_thermal": {
            "current_state": {
              "code": get().solarthermie,
              "installation_year":  get().solarthermie_datum,
              "roof_pitch": get().solarthermie_roof_pitch,
              "collector_area":  get().solarthermie_kollektorflaeche,
              "orientation": get().solarthermie_orientation,
            },
           "planned_state": {
              "code": get().solarthermie_future,
              "cost_estimate":  get().solarthermie_kosten,
              "roof_pitch": get().solarthermie_roof_pitch_future,
              "orientation": get().solarthermie_orientation_future,
              "collector_area":  get().solarthermie_kollektorflaeche_future,
            }
          },
          "photovoltaics": {
           "current_state": {
              "code": get().photovoltaik,
              "installation_year": get().photovoltaik_datum,
              "roof_pitch": get().photovoltaik_roof_pitch,
              "orientation": get().photovoltaik_orientation,
              "annual_yield_kWh": get().photovoltaik_annual_yield_kWh,
              "battery_storage": get().photovoltaik_battery_storage 
            },
           "planned_state": {
              "code": get().photovoltaik_future,
              "cost_estimate":  get().photovoltaik_kosten,
              "roof_pitch": get().photovoltaik_roof_pitch_future,
              "orientation": get().photovoltaik_orientation_future,
              "battery_storage":get().photovoltaik_battery_storage_future 
            }
      }
    };

    remove_undefined_from_object(renovation_measures);

    let heizung = get().heizung;
    let oelverbrauch = get().oelverbrauch / 0.1;
    let gasverbrauch = get().gasverbrauch;
    let gasverbrauch_m3 = get().gasverbrauch_m3 / 0.0952;
    let holzverbrauch = get().holzverbrauch * 4.8;
    let holzverbrauch_t = get().holzverbrauch_t * 4800;
    let holzverbrauch_m3 = get().holzverbrauch_m3 * 3.120;
    let stromverbrauchwaermepumpe = get().stromverbrauchwaermepumpe;

    let verbrauch_kwh = 0;
    switch (heizung) {
      case "heating_system_current_0":
      case "heating_system_current_8":
      case "heating_system_current_10":
         verbrauch_kwh = oelverbrauch;
         break;
      case "heating_system_current_1":
      case "heating_system_current_7":
      case "heating_system_current_9":
         if(get().verbrauchseinheit_gas == "m3"){
            verbrauch_kwh = gasverbrauch_m3;
         }else {
            verbrauch_kwh = gasverbrauch;
         }
         break;
      case "heating_system_current_2":
         if(get().verbrauchseinheit_holz == "t"){
            verbrauch_kwh = holzverbrauch_t;
         }else if(get().verbrauchseinheit_holz == "m3"){
            verbrauch_kwh = holzverbrauch_m3;
         }else {
            verbrauch_kwh = holzverbrauch;
         }
         break;
      case "heating_system_current_3":
      case "heating_system_current_4":
         verbrauch_kwh = stromverbrauchwaermepumpe;
         break;
      case "heating_system_current_6":
         verbrauch_kwh = gasverbrauch;
         break;
   }

    const body = { 
      "annual_energy_consumption_kWh": verbrauch_kwh, 
      "annual_electricity_consumption_kWh":  get().stromverbrauch,
      renovation_measures
    };

    const request = new ESRCalculateEnergyCosts(session_token,body);
    const response = await request.request().catch((e) => {
        console.error(e);
    });

    if (response){
      const rounded = Object.keys(response).reduce((acc, key) => ({...acc, [key] : round_to_10(response[key] || 0)}),{});
      set((_)=>({
        ...rounded,
        energy_efficiency_class_default_level: response.energy_efficiency_class_default_level,
        energy_efficiency_class_adjusted_level: response.energy_efficiency_class_adjusted_level,
        energy_efficiency_class_planned_level: response.energy_efficiency_class_planned_level,
      }));
    }
  },
  
  api_calculate_energy_costs_dummy: async () =>{
    const session_token = get().session_token;
    if(session_token == undefined){
      return;
    }

    const body = {
        "annual_electricity_consumption_kWh": 3600,
        "annual_energy_consumption_kWh": 3600,
        "renovation_measures": {
          "facade_insulation": {
            "planned_state": {
              "code": "facade_insulation_planned_1"
            }
          },
          "floor_insulation": {
            "planned_state": {
              "code": "floor_insulation_planned_3"
            }
          },
          "heating_system": {
            "current_state": {
              "code": "heating_system_current_0"
            },
            "planned_state": {
              "code": "heating_system_planned_2"
            }
          },
          "photovoltaics": {
            "planned_state": {
              "code": "photovoltaics_planned_1"
            }
          },
          "roof_insulation": {
            "planned_state": {
            "code": "roof_insulation_planned_5"
            }
          },
          "solar_thermal": {
            "current_state": {},
            "planned_state": {
              "code": "solar_thermal_planned_1",
              "collector_area": "5"
            }
          },
          "ventilation_system": {
            "planned_state": {
              "code": "ventilation_system_planned_2"
            }
          },
          "window": {
            "planned_state": {
              "code": "window_planned_1"
            }
          }
        }
      };

    const request = new ESRCalculateEnergyCosts(session_token,body);
    const response = await request.request().catch((e) => {
        console.error(e);
    });

    if (response){}
  },
  

  api_basic_data: async () =>{
    const session_token = get().session_token;
    if(session_token == undefined){
      return;
    }

    const request = new ESRBasicData(
      session_token,
      get().gebaeude_typ as BuildingTypeKeys,
      get().baujahr,
      get().postleitzahl,
      get().wohnflaeche,
      get().wohneinheiten,
      get().vollgeschosse,
      get().heizung,
      "DE",
      0,//(get().beheizte_segmente as HeatedSegment)[0], 
      100,//(get().beheizte_segmente as HeatedSegment)[1], 
      0,//(get().beheizte_segmente as HeatedSegment)[2],
      get().privat,
    );
    const response = await request.request().catch((e) => {
        console.error(e);
    });
    if (response){
      const heating_demand_kwh = response.heating_demand_kwh;
	    const electricity_consumption_kwh = response.electricity_consumption_kwh;

      set((_) => ({ 
        heizung_tmp: get().heizung,
        ...response,
        oelverbrauch: round_to_10(heating_demand_kwh * 0.1),
        gasverbrauch: round_to_10(heating_demand_kwh),
        gasverbrauch_m3: round_to_10(heating_demand_kwh * 0.0952),
        holzverbrauch: round_to_10(heating_demand_kwh / 4.8),
        holzverbrauch_t: round_to_01(heating_demand_kwh / 4800),
        holzverbrauch_m3: round_to_10(heating_demand_kwh / 3.120),
        stromverbrauchwaermepumpe: round_to_10(heating_demand_kwh),
        stromverbrauch: round_to_10(electricity_consumption_kwh),
       }));
    }
  },

  api_check_zip: async (plz) =>{
    const session_token = get().session_token;
    if(session_token == undefined){
      return;
    }

    const request = new ESRCheckZip(
      session_token,
      plz,
    );

    const response = await request.request().catch((e) => {
        console.error(e);
    });

    set((_)=>({region_name: response.region_name}))
    return response && response.region_name;
  },

  api_financial_plan: async () => {
    const request = new ESRFinancialPlan(get().session_token);
    const response = await request.request().catch((e) => {
        console.error(e);
    });
    if (response ){
      set((_) => ({...response}));
    }
  },

  api_financial_plan_dummy: async () => {
    await get().api_calculate_energy_costs_dummy();
    const request = new ESRFinancialPlan(get().session_token);
    const response = await request.request().catch((e) => {
        console.error(e);
    });
    if (response ){
      set((_) => ({financial_plan_dummy: {version: 1, ...response.financial_plan}}));
    }
  },

  api_profitability_calculation: async () => {
    const kostensteigerung = get().kostensteigerung_energie || 0;
    const co2_price = get().co2_price || 0;
    const request = new ESRProfitabilityCalculation(get().session_token, kostensteigerung, co2_price);
    const response = await request.request().catch((e) => {
        console.error(e);
    });
    if (response){
      set((_) => ({...response}));
    }
  },

  api_recalculate: async () => {
    console.log("RECALCULATE");
    await get().api_calculate_energy_costs();
    await get().api_financial_plan();
    await get().api_profitability_calculation();
  },


  api_get_costs: async (type: string, code: string) => {
    const request = new ESRGetCosts(get().session_token,  {code});
    const response = await request.request().catch((e) => {
        console.error(e);
    });

    if(!response) {return;}

    const financial_plan_dummy = {...get().financial_plan_dummy};
    financial_plan_dummy.version += 1;
    financial_plan_dummy[type]["total_costs"] = response.costs;

    if (financial_plan_dummy){
      //console.log(type, code, financial_plan_dummy);
      set((_) => ({financial_plan_dummy}));
    }
  },
});
